import React, { useContext, useEffect } from 'react';
import { Form } from 'antd';
import type { FormItemProps } from 'antd';
import { FormShellContext, SectionContext, pathKey } from './context';

export type FieldProps = Omit<FormItemProps, 'extra'> & {
    /**
     * Guidance shown between the label and the control. antd wires it into
     * the control's aria-describedby; the CSS moves it above the input.
     */
    hint?: React.ReactNode;
    /** width in a 12-column section grid (default: full width) */
    span?: number;
};

/**
 * A form field. Thin on purpose: antd's Form.Item still owns the value,
 * the rules and the error state — this adds the layout span and tells the
 * shell which section the field lives in so a failed submit can point at it.
 */
const Field = ({ hint, span = 12, children, ...itemProps }: FieldProps) => {
    const shell = useContext(FormShellContext);
    const sectionId = useContext(SectionContext);
    const key = pathKey(itemProps.name);

    const register = shell?.registerField;
    const unregister = shell?.unregisterField;
    useEffect(() => {
        if (!register || !unregister || !sectionId || !key) return undefined;
        register(key, sectionId);
        return () => unregister(key);
    }, [register, unregister, key, sectionId]);

    return (
        <Form.Item
            {...itemProps}
            extra={hint}
            /* the class carries the same width as the style, so the breakpoints
               can widen a narrow field without having to out-specify an inline
               rule for every span there is */
            className={`nf-col-${span}${itemProps.className ? ` ${itemProps.className}` : ''}`}
            style={{ gridColumn: `span ${span} / span ${span}`, ...itemProps.style }}
        >
            {children}
        </Form.Item>
    );
};

export default Field;
